import React, { useMemo, useState } from 'react';
import { Search, ChevronDown, Lock, Play, Clock, Crown, ArrowRight, BookOpen, SearchX } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface TutorialSidebarProps {
  categories: any[];
  allTutorials: any[];
  currentTutorialId: any;
  onTutorialClick: (tutorial: any) => void;
  isLoading?: boolean;
}

export default function TutorialSidebar({ 
  categories, 
  allTutorials, 
  currentTutorialId, 
  onTutorialClick,
  isLoading 
}: TutorialSidebarProps) {
  const [query, setQuery] = useState('');
  const [expandedModules, setExpandedModules] = useState<Set<any>>(new Set());

  // Logic to group tutorials by category (Modules)
  const modules = useMemo(() => {
    return categories.map(cat => {
      const catId = cat.id || cat.Id;
      const tutorials = allTutorials.filter((t: any) => {
        const tCatId = t.categoryId || t.CategoryId || t.category?.id || t.Category?.Id;
        return Number(tCatId) === Number(catId);
      });
      return { ...cat, tutorials };
    }).filter(m => m.tutorials.length > 0 || m.children?.length > 0);
  }, [categories, allTutorials]);

  const toggleModule = (id: any) => {
    setExpandedModules(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredModules = useMemo(() => {
    if (!query) return modules;
    const q = query.toLowerCase();
    return modules.map(m => ({
      ...m,
      tutorials: m.tutorials.filter((t: any) => (t.title || t.Title || '').toLowerCase().includes(q))
    })).filter(m => m.tutorials.length > 0);
  }, [modules, query]);

  return (
    <aside className="w-[320px] h-full bg-white border-r border-slate-100 flex flex-col shrink-0 transition-all duration-300">
      
      {/* Sidebar Header: Logo & Search */}
      <div className="p-6 pb-4">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-black rounded-2xl flex items-center justify-center text-white shadow-lg">
             <BookOpen size={20} strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-tight text-slate-950 leading-none">Lukos.</h1>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">LMS Platform</p>
          </div>
        </div>

        {/* Search Bar (Learnfy style) */}
        <div className="relative group">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-slate-900 transition-colors" />
          <input 
            type="text" 
            placeholder="Search tutorials..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full h-11 pl-12 pr-4 bg-slate-50 border border-slate-100 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 outline-none focus:bg-white focus:border-slate-200 focus:ring-4 focus:ring-slate-50 transition-all"
          />
        </div>
      </div>

      {/* Upgrade Banner (Learnfy style) */}
      <div className="px-6 mb-8">
         <div className="bg-slate-950 rounded-2xl p-4 flex items-center justify-between group cursor-pointer hover:bg-slate-900 transition-all">
            <div className="flex items-center gap-3">
               <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                  <Lock size={14} className="text-white" />
               </div>
               <span className="text-white text-[13px] font-bold">Upgrade to premium</span>
            </div>
            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-black group-hover:scale-110 transition-transform">
               <ArrowRight size={16} strokeWidth={2.5} />
            </div>
         </div>
      </div>

      {/* Modules List (Accordion) */}
      <div className="flex-grow overflow-y-auto px-4 no-scrollbar">
         <div className="mb-4 px-2">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Course Modules</p>
         </div>

         {isLoading ? (
            <div className="space-y-4 px-2">
               {[1,2,3].map(i => <div key={i} className="h-10 bg-slate-50 rounded-lg animate-pulse" />)}
            </div>
         ) : filteredModules.length > 0 ? (
            <div className="space-y-2 pb-10">
               {filteredModules.map((module) => {
                  const mId = module.id || module.Id;
                  const mName = module.name || module.Name;
                  const isExpanded = expandedModules.has(mId);
                  
                  return (
                    <div key={mId} className="space-y-1">
                       <button 
                         onClick={() => toggleModule(mId)}
                         className={`w-full flex items-center justify-between px-3 py-3 rounded-xl transition-all ${isExpanded ? 'bg-slate-50 text-slate-900' : 'text-slate-500 hover:bg-slate-50'}`}
                       >
                          <span className="text-[14px] font-bold tracking-tight">{mName}</span>
                          <ChevronDown size={14} className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                       </button>

                       <AnimatePresence>
                          {isExpanded && (
                            <motion.div 
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden bg-slate-50/30 rounded-xl"
                            >
                               {module.tutorials.map((t: any) => {
                                  const tId = t.id || t.Id;
                                  const isActive = Number(currentTutorialId) === Number(tId);
                                  return (
                                    <button 
                                      key={tId}
                                      onClick={() => onTutorialClick(t)}
                                      className={`w-full flex flex-col gap-1.5 px-3 py-4 rounded-xl text-left transition-all relative ${isActive ? 'bg-white shadow-sm ring-1 ring-slate-100' : 'hover:bg-white/50'}`}
                                    >
                                       {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-black rounded-r-full" />}
                                       <span className={`text-[13px] font-bold ${isActive ? 'text-black font-extrabold' : 'text-slate-600'}`}>{t.title || t.Title}</span>
                                       <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-3">
                                             <div className="flex items-center gap-1 text-[11px] font-medium text-slate-400">
                                                <Clock size={10} />
                                                {t.duration || '6:18'}
                                             </div>
                                             {t.isFree !== false ? (
                                                <span className="text-[10px] font-bold text-emerald-500 bg-emerald-50 px-1.5 py-0.5 rounded">Free</span>
                                             ) : null}
                                          </div>
                                          {t.isFree === false && <Lock size={12} className="text-slate-300" />}
                                       </div>
                                    </button>
                                  );
                               })}
                            </motion.div>
                          )}
                       </AnimatePresence>
                    </div>
                  );
               })}
            </div>
         ) : (
            <div className="py-20 flex flex-col items-center justify-center text-center px-6">
               <SearchX size={32} className="text-slate-200 mb-4" />
               <p className="text-sm font-bold text-slate-400 leading-tight">No lessons found matching your search.</p>
            </div>
         )}
      </div>

      {/* Sidebar Footer Link */}
      <div className="p-6 border-t border-slate-50 flex flex-col gap-3">
         <button className="flex items-center justify-between text-[13px] font-bold text-slate-400 hover:text-slate-900 transition-colors">
            Links
         </button>
         <button className="flex items-center justify-between text-[13px] font-bold text-slate-400 hover:text-slate-900 transition-colors">
            Questions
            <ArrowRight size={14} className="-rotate-45" />
         </button>
      </div>
    </aside>
  );
}
